import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  Users, Crown, Trophy, Target, Zap, Heart, Timer, 
  ArrowLeft, Share2, Copy, Check, Sparkles, Gamepad2,
  Eye, PartyPopper, Swords, Shield, Crown as CrownIcon,
  Cross, BookOpen, Star
} from 'lucide-react'
import { 
  joinGameRoom, subscribeToRoom, updatePlayerScore, 
  setPlayerReady, leaveRoom, createGameRoom, startGame,
  subscribeToGameState, updateGameState
} from '../utils/multiplayerAPI.js'
import Leaderboard from '../components/Leaderboard.jsx'
import SpectatorMode from '../components/SpectatorMode.jsx'
import PartyMode from '../components/PartyMode.jsx'
import './Multiplayer.css'

// Game stages for Easter story
const GAME_STAGES = [
  {
    id: 'love',
    name: 'Guardian of Love',
    icon: Heart,
    color: '#ff6b9d',
    prompt: 'What is the first big truth of Easter?',
    clue: 'It tells us why the story is good news for everybody.',
    options: [
      { label: 'God loves the whole world', correct: true, power: 'HEAL' },
      { label: 'Nobody mattered', correct: false },
      { label: 'The story has no meaning', correct: false },
    ]
  },
  {
    id: 'cross',
    name: 'Sentinel of Sacrifice',
    icon: Cross,
    color: '#ff4757',
    prompt: 'Why did Jesus go to the cross?',
    clue: 'Think about sacrifice, rescue, and love.',
    options: [
      { label: 'To show love and save us from sin', correct: true, power: 'SHIELD' },
      { label: 'To win a game', correct: false },
      { label: 'To hide from everyone', correct: false },
    ]
  },
  {
    id: 'tomb',
    name: 'Keeper of the Seal',
    icon: BookOpen,
    color: '#74b9ff',
    prompt: 'What did the women discover on the third day?',
    clue: 'This surprise changed history forever.',
    options: [
      { label: 'The tomb was empty', correct: true, power: 'BURST' },
      { label: 'The stone was made of gold', correct: false },
      { label: 'The garden disappeared', correct: false },
    ]
  },
  {
    id: 'rise',
    name: 'Champion of Life',
    icon: CrownIcon,
    color: '#ffd700',
    prompt: 'What is the Easter message for us today?',
    clue: 'It is the hope we celebrate together.',
    options: [
      { label: 'Jesus is risen and gives new life', correct: true, power: 'VICTORY' },
      { label: 'The story ended in sadness', correct: false },
      { label: 'Hope only belongs to the past', correct: false },
    ]
  }
]

const POWER_UPS = {
  HEAL: { icon: Heart, color: '#ff6b9d', name: 'Love Shield' },
  SHIELD: { icon: Shield, color: '#74b9ff', name: 'Faith Guard' },
  BURST: { icon: Zap, color: '#ffd700', name: 'Hope Burst' },
  VICTORY: { icon: Trophy, color: '#00d26a', name: 'Eternal Victory' }
}

export default function Multiplayer() {
  const navigate = useNavigate()
  const { roomId: urlRoomId } = useParams()
  
  // Player state
  const [playerName, setPlayerName] = useState('')
  const [playerId, setPlayerId] = useState(null)
  const [roomId, setRoomId] = useState(urlRoomId || null)
  const [isHost, setIsHost] = useState(false)
  
  // Game state
  const [gameState, setGameState] = useState('lobby') // lobby, playing, paused, finished
  const [players, setPlayers] = useState([])
  const [currentStage, setCurrentStage] = useState(0)
  const [score, setScore] = useState(0)
  const [powers, setPowers] = useState([])
  const [timeLeft, setTimeLeft] = useState(30)
  const [combo, setCombo] = useState(0)
  
  // UI state
  const [showLeaderboard, setShowLeaderboard] = useState(false)
  const [isSpectator, setIsSpectator] = useState(false)
  const [partyMode, setPartyMode] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const [feedback, setFeedback] = useState('')
  const [feedbackType, setFeedbackType] = useState('idle')
  
  const timerRef = useRef(null)
  const subscriptionRef = useRef(null)

  // Initialize player
  useEffect(() => {
    const storedName = localStorage.getItem('playerName')
    const storedId = localStorage.getItem('playerId')
    if (storedName) setPlayerName(storedName)
    if (storedId) setPlayerId(storedId)
  }, [])

  // Join or create room
  const joinRoom = useCallback(async () => {
    if (!playerName.trim()) {
      setError('Please enter your name')
      return
    }
    
    try {
      let newRoomId = roomId
      let isNewRoom = false
      
      if (!newRoomId) {
        // Create new room
        const room = await createGameRoom({ 
          hostName: playerName,
          partyMode: partyMode,
          maxPlayers: partyMode ? 8 : 4
        })
        newRoomId = room.id
        isNewRoom = true
      }
      
      // Join room
      const player = await joinGameRoom(newRoomId, playerName)
      setPlayerId(player.id)
      setRoomId(newRoomId)
      setIsHost(isNewRoom)
      
      // Save to localStorage
      localStorage.setItem('playerName', playerName)
      localStorage.setItem('playerId', player.id)
      
      // Subscribe to room updates
      subscriptionRef.current = subscribeToRoom(newRoomId, (payload) => {
        handleRoomUpdate(payload)
      })
      
      // Update URL
      window.history.replaceState({}, '', `/multiplayer/${newRoomId}`)
      
    } catch (err) {
      setError(err.message)
    }
  }, [playerName, roomId, partyMode])

  // Handle room updates
  const handleRoomUpdate = useCallback((payload) => {
    const { eventType, new: newData, old: oldData } = payload
    
    switch (eventType) {
      case 'INSERT':
        setPlayers(prev => [...prev, newData])
        break
      case 'UPDATE':
        setPlayers(prev => prev.map(p => p.id === newData.id ? newData : p))
        if (newData.id === playerId) {
          setScore(newData.score || 0)
          setPowers(newData.powers || [])
        }
        break
      case 'DELETE':
        setPlayers(prev => prev.filter(p => p.id !== oldData.id))
        break
    }
  }, [playerId])

  // Game timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Time's up - move to next stage or end game
            handleTimeUp()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    
    return () => clearInterval(timerRef.current)
  }, [gameState, timeLeft])

  const handleTimeUp = async () => {
    if (currentStage < GAME_STAGES.length - 1) {
      setCurrentStage(prev => prev + 1)
      setTimeLeft(30)
      setCombo(0)
    } else {
      // Game finished
      setGameState('finished')
      await updateGameState(roomId, { status: 'finished' })
    }
  }

  // Handle answer selection
  const handleAnswer = async (option, index) => {
    if (!option.correct) {
      setFeedback('Wrong! Try again!')
      setFeedbackType('error')
      setCombo(0)
      setTimeLeft(prev => Math.max(0, prev - 5)) // Penalty
      return
    }

    // Correct answer
    const timeBonus = Math.floor(timeLeft / 5)
    const comboBonus = combo * 10
    const points = 100 + timeBonus + comboBonus
    
    const newScore = score + points
    const newCombo = combo + 1
    const newPowers = option.power ? [...powers, option.power] : powers
    
    setScore(newScore)
    setCombo(newCombo)
    setPowers(newPowers)
    setFeedback(`+${points} points! Combo x${newCombo}!`)
    setFeedbackType('success')

    // Update server
    await updatePlayerScore(roomId, playerId, {
      score: newScore,
      combo: newCombo,
      powers: newPowers,
      stageCompleted: currentStage
    })

    // Move to next stage
    setTimeout(() => {
      if (currentStage < GAME_STAGES.length - 1) {
        setCurrentStage(prev => prev + 1)
        setTimeLeft(30)
        setFeedback('')
      } else {
        setGameState('finished')
      }
    }, 1500)
  }

  // Start game (host only)
  const handleStartGame = async () => {
    if (!isHost) return
    
    await startGame(roomId)
    setGameState('playing')
    setCurrentStage(0)
    setTimeLeft(30)
    setScore(0)
    setCombo(0)
    setPowers([])
  }

  // Leave room
  const handleLeave = async () => {
    if (playerId && roomId) {
      await leaveRoom(roomId, playerId)
    }
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
    }
    navigate('/')
  }

  // Copy room link
  const copyRoomLink = () => {
    const link = `${window.location.origin}/multiplayer/${roomId}`
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Get sorted players for leaderboard
  const sortedPlayers = [...players].sort((a, b) => (b.score || 0) - (a.score || 0))
  const currentStageData = GAME_STAGES[currentStage]
  const StageIcon = currentStageData?.icon || Star

  // Render lobby
  if (gameState === 'lobby') {
    return (
      <main className="multiplayer-lobby">
        <div className="lobby-background">
          <div className="floating-eggs">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="floating-egg" style={{ 
                animationDelay: `${i * 0.5}s`,
                left: `${Math.random() * 100}%`
              }} />
            ))}
          </div>
        </div>
        
        <div className="lobby-container">
          <div className="lobby-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <ArrowLeft size={20} />
              Back
            </button>
            <h1 className="lobby-title">
              <Gamepad2 size={32} />
              Multiplayer Easter Quest
            </h1>
          </div>

          {!roomId ? (
            <div className="lobby-setup">
              <div className="name-input-section">
                <label>Enter Your Name</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Your name..."
                  maxLength={20}
                  className="name-input"
                />
              </div>

              <div className="game-modes">
                <button 
                  className={`mode-btn ${!partyMode ? 'active' : ''}`}
                  onClick={() => setPartyMode(false)}
                >
                  <Target size={24} />
                  <span>Competitive</span>
                  <small>Up to 4 players, fastest wins</small>
                </button>
                <button 
                  className={`mode-btn ${partyMode ? 'active' : ''}`}
                  onClick={() => setPartyMode(true)}
                >
                  <PartyPopper size={24} />
                  <span>Party Mode</span>
                  <small>Up to 8 players, team fun</small>
                </button>
              </div>

              <div className="lobby-actions">
                <button className="create-btn" onClick={joinRoom}>
                  <Sparkles size={20} />
                  {roomId ? 'Join Game' : 'Create Game Room'}
                </button>
                <button className="spectator-btn" onClick={() => setIsSpectator(true)}>
                  <Eye size={20} />
                  Join as Spectator
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}
            </div>
          ) : (
            <div className="waiting-room">
              <div className="room-info">
                <h2>Game Room</h2>
                <div className="room-code">
                  <span>Room Code: <strong>{roomId}</strong></span>
                  <button className="copy-btn" onClick={copyRoomLink}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="share-text">Share this code with friends to invite them!</p>
              </div>

              <div className="players-list">
                <h3>Players ({players.length}/{partyMode ? 8 : 4})</h3>
                {players.map((player, index) => (
                  <div key={player.id} className={`player-item ${player.id === playerId ? 'you' : ''}`}>
                    <span className="player-rank">#{index + 1}</span>
                    <span className="player-name">
                      {player.name}
                      {player.id === playerId && <span className="you-badge">YOU</span>}
                    </span>
                    <span className={`ready-status ${player.ready ? 'ready' : ''}`}>
                      {player.ready ? 'Ready!' : 'Waiting...'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="lobby-actions">
                {isHost ? (
                  <button 
                    className="start-btn" 
                    onClick={handleStartGame}
                    disabled={players.length < 1}
                  >
                    <Swords size={20} />
                    Start Game
                  </button>
                ) : (
                  <button 
                    className="ready-btn"
                    onClick={() => setPlayerReady(roomId, playerId, true)}
                  >
                    <Check size={20} />
                    I'm Ready!
                  </button>
                )}
                <button className="leave-btn" onClick={handleLeave}>
                  <ArrowLeft size={20} />
                  Leave Room
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    )
  }

  // Render spectator mode
  if (isSpectator) {
    return <SpectatorMode roomId={roomId} players={players} onLeave={handleLeave} />
  }

  // Render party mode
  if (partyMode) {
    return (
      <PartyMode 
        roomId={roomId}
        playerId={playerId}
        players={players}
        gameState={gameState}
        currentStage={currentStage}
        score={score}
        onLeave={handleLeave}
      />
    )
  }

  // Render main game
  return (
    <main className="multiplayer-game">
      {/* Background Effects */}
      <div className="game-background">
        <div className="grid-overlay" />
        <div className="glow-effect" style={{ '--stage-color': currentStageData?.color }} />
      </div>

      {/* Game HUD */}
      <header className="game-hud">
        <div className="hud-left">
          <div className="stage-info">
            <span className="stage-label">STAGE {currentStage + 1}/{GAME_STAGES.length}</span>
            <div className="stage-dots">
              {GAME_STAGES.map((_, i) => (
                <div key={i} className={`stage-dot ${i === currentStage ? 'active' : i < currentStage ? 'completed' : ''}`} />
              ))}
            </div>
          </div>
        </div>

        <div className="hud-center">
          <div className="timer">
            <Timer size={20} />
            <span className={timeLeft <= 10 ? 'urgent' : ''}>{timeLeft}s</span>
          </div>
        </div>

        <div className="hud-right">
          <button className="leaderboard-toggle" onClick={() => setShowLeaderboard(!showLeaderboard)}>
            <Trophy size={20} />
            <span>#{sortedPlayers.findIndex(p => p.id === playerId) + 1 || '-'}</span>
          </button>
        </div>
      </header>

      {/* Leaderboard Overlay */}
      {showLeaderboard && (
        <Leaderboard 
          players={sortedPlayers} 
          currentPlayerId={playerId}
          onClose={() => setShowLeaderboard(false)}
        />
      )}

      {/* Game Arena */}
      <section className="game-arena">
        <div className="boss-container">
          <div className="boss-avatar" style={{ '--boss-color': currentStageData?.color }}>
            <StageIcon size={80} />
          </div>
          <h2 className="boss-name">{currentStageData?.name}</h2>
        </div>

        {/* Question Card */}
        <div className="question-card">
          <h3 className="question-text">{currentStageData?.prompt}</h3>
          <p className="question-clue">{currentStageData?.clue}</p>
        </div>

        {/* Answer Options */}
        <div className="answer-grid">
          {currentStageData?.options.map((option, index) => (
            <button
              key={index}
              className="answer-btn"
              onClick={() => handleAnswer(option, index)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className="btn-key">{index + 1}</span>
              <span className="btn-text">{option.label}</span>
              {option.power && powers.includes(option.power) && (
                <span className="power-indicator">
                  {(() => {
                    const Icon = POWER_UPS[option.power]?.icon
                    return Icon ? <Icon size={14} /> : null
                  })()}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Feedback Message */}
        {feedback && (
          <div className={`feedback-message ${feedbackType}`}>
            {feedbackType === 'success' ? <Sparkles size={20} /> : <Target size={20} />}
            <span>{feedback}</span>
          </div>
        )}
      </section>

      {/* Bottom Stats */}
      <footer className="game-footer">
        <div className="stat-box">
          <span className="stat-label">SCORE</span>
          <span className="stat-value">{score.toLocaleString()}</span>
        </div>
        <div className="stat-box">
          <span className="stat-label">COMBO</span>
          <span className="stat-value" style={{ color: combo > 2 ? '#ffd700' : '#fff' }}>
            x{combo}
          </span>
        </div>
        <div className="stat-box powers">
          <span className="stat-label">POWERS</span>
          <div className="power-icons">
            {powers.map((power, i) => (
              <span key={i} className="mini-power" style={{ color: POWER_UPS[power]?.color }}>
                {(() => {
                  const Icon = POWER_UPS[power]?.icon
                  return Icon ? <Icon size={14} /> : null
                })()}
              </span>
            ))}
          </div>
        </div>
        <button className="leave-game-btn" onClick={handleLeave}>
          <ArrowLeft size={16} />
          Exit
        </button>
      </footer>

      {/* Game Finished Overlay */}
      {gameState === 'finished' && (
        <div className="game-over-overlay">
          <div className="game-over-modal">
            <div className="winner-crown">
              <Crown size={60} />
            </div>
            <h2>Quest Complete!</h2>
            <div className="final-stats">
              <div className="final-stat">
                <span className="final-label">Final Score</span>
                <span className="final-value">{score.toLocaleString()}</span>
              </div>
              <div className="final-stat">
                <span className="final-label">Rank</span>
                <span className="final-value">
                  #{sortedPlayers.findIndex(p => p.id === playerId) + 1}
                </span>
              </div>
              <div className="final-stat">
                <span className="final-label">Powers</span>
                <span className="final-value">{powers.length}/4</span>
              </div>
            </div>
            <button className="play-again-btn" onClick={() => navigate('/multiplayer')}>
              <Sparkles size={20} />
              Play Again
            </button>
          </div>
        </div>
      )}
    </main>
  )
}
